import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Play, Quote, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";

const FOUNDER_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_vlyt-launch/artifacts/hk7n4xk1_5e4bbdd8-9ffd-4f65-b43c-51de29e39596.jpeg";
const FOUNDER_AVATAR = "https://customer-assets-agu9un31.emergentagent.net/job_vlyt-launch/artifacts/rycvzply_397107ac-dbb4-4624-a9e6-f09775dcb539.jpeg";

// H.264/AAC MP4, faststart, ~8 MB. Served from /app/frontend/public.
const VIDEO_URL = "/founder-story.mp4";

export default function FounderStory() {
  const { ref, inView } = useReveal({ threshold: 0.2 });
  const [open, setOpen] = React.useState(false);
  const [videoErrored, setVideoErrored] = React.useState(false);

  React.useEffect(() => {
    if (open) setVideoErrored(false);
  }, [open]);

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-32 border-b border-[color:var(--vlyt-border)]"
      data-testid="founder-story-section"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
        {/* Video card / photo poster */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="founder-story-play"
          aria-label="Watch the founder story"
          className={`reveal ${inView ? "in" : ""} group relative block w-full rounded-[24px] overflow-hidden border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-1)] focus:outline-none`}
        >
          <div className="relative aspect-[16/10] w-full">
            <img
              src={FOUNDER_IMG}
              alt="Miesindou Alagha, founder of Vlyt Technologies"
              className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              loading="lazy"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, transparent 40%, color-mix(in srgb, var(--vlyt-base) 88%, transparent) 100%)",
              }}
            />
            {/* Play button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative flex items-center justify-center">
                <span className="absolute h-24 w-24 rounded-full bg-[color:var(--vlyt-lime)] opacity-30 blur-2xl group-hover:opacity-60 transition-opacity" aria-hidden="true" />
                <span className="relative h-20 w-20 rounded-full bg-[color:var(--vlyt-lime)] flex items-center justify-center ring-8 ring-[color:var(--vlyt-lime)]/10 group-hover:ring-[color:var(--vlyt-lime)]/25 transition">
                  <Play size={26} fill="#0E0E12" color="#0E0E12" className="ml-1" strokeWidth={0} />
                </span>
              </div>
            </div>
            {/* Bottom overlay copy */}
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 flex items-end justify-between gap-4">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-[color:var(--vlyt-purple-soft)] mb-2">
                  Watch · 75 sec
                </div>
                <div className="font-display text-[22px] md:text-[26px] leading-tight text-[color:var(--vlyt-text)]">
                  Meet Miesindou. In his own voice.
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2 text-[13px] font-mono uppercase tracking-widest text-[color:var(--vlyt-muted)]">
                Play
                <ArrowUpRight size={14} />
              </div>
            </div>
          </div>
        </button>

        {/* Quote panel */}
        <div className={`reveal ${inView ? "in" : ""}`} style={{ transitionDelay: "140ms" }}>
          <div className="text-eyebrow mb-6">Note from the founder</div>
          <Quote size={32} className="text-lime mb-5" strokeWidth={2.2} />
          <blockquote className="font-display text-[22px] md:text-[26px] leading-[1.35] tracking-tight text-[color:var(--vlyt-text)]">
            &ldquo;I built Vlyt because I was tired of losing my mornings to a shuttle that never
            came. This is not a ride app. It is a promise: your time is worth protecting.&rdquo;
          </blockquote>
          <div className="mt-8 flex items-center gap-4">
            <img
              src={FOUNDER_AVATAR}
              alt=""
              aria-hidden="true"
              className="h-11 w-11 rounded-full object-cover object-center ring-1 ring-[color:var(--vlyt-border)]"
            />
            <div>
              <div className="font-display text-[15px]">Miesindou Alagha</div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[color:var(--vlyt-muted)]">
                Founder · Strategic Architect
              </div>
            </div>
          </div>
          <div className="mt-8">
            <Link
              to="/about/miesindou"
              data-testid="founder-story-read-more"
              className="inline-flex items-center gap-2 text-[14px] font-mono uppercase tracking-widest text-[color:var(--vlyt-purple-soft)] hover:text-lime"
            >
              Read the founder profile <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      {/* Video dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0E0E12] border border-[color:var(--vlyt-border)] text-[#F0F0F5] max-w-3xl p-0 overflow-hidden">
          <VisuallyHidden.Root>
            <DialogTitle>Founder story · Miesindou Alagha</DialogTitle>
            <DialogDescription>A short video introduction from Miesindou Alagha, founder of Vlyt Technologies.</DialogDescription>
          </VisuallyHidden.Root>
          <div className="aspect-video w-full bg-[#0E0E12] flex items-center justify-center relative">
            {!videoErrored ? (
              <video
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="h-full w-full object-contain bg-[#0E0E12]"
                poster={FOUNDER_IMG}
                onError={() => setVideoErrored(true)}
                data-testid="founder-story-video"
              >
                <source src="/founder-story.mp4" type="video/mp4" />
                <source src="/founder-story.webm" type="video/webm" />
                Your browser does not support this video format.
              </video>
            ) : (
              <div className="text-center px-8">
                <div className="mx-auto h-16 w-16 rounded-full bg-[rgba(191,255,0,0.08)] flex items-center justify-center ring-1 ring-[color:var(--vlyt-lime)] mb-5">
                  <Play size={22} className="text-lime ml-1" strokeWidth={2.4} />
                </div>
                <div className="font-display text-[24px] text-[#F0F0F5]">Video cannot play in this browser</div>
                <div className="text-[#A0A0AC] mt-3 max-w-md mx-auto">
                  Try a different browser, or open the video directly.
                </div>
                <a
                  href="/founder-story.mp4"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-lime mt-6"
                  data-testid="founder-story-download"
                >
                  Open the video
                </a>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
